import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { notificationsApi } from '../lib/api';
import { usePushNotifications } from '../hooks/usePushNotifications';
import LoadingScreen from '../components/common/LoadingScreen';
import EmptyState from '../components/common/EmptyState';
import TimeAgo from '../components/common/TimeAgo';
import Logo from '../components/common/Logo';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Bell, AlertTriangle, Info, RefreshCw, ArrowLeft, LogIn, BellRing, X, Map } from 'lucide-react';
import { toast } from 'sonner';

const PublicAlertsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showPushBanner, setShowPushBanner] = useState(true);
  
  const {
    isSupported,
    permission,
    isSubscribed,
    isLoading: pushLoading,
    subscribe
  } = usePushNotifications();

  const fetchNotifications = async () => {
    try {
      const response = await notificationsApi.getAll();
      setNotifications(response.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchNotifications();
  };

  const handleEnablePush = async () => {
    const result = await subscribe();
    if (result.success) {
      toast.success('Push notifications enabled! You will receive alerts even when this page is closed.');
      setShowPushBanner(false);
    } else {
      if (result.error === 'Permission denied') {
        toast.error('Please allow notifications in your browser settings');
      } else {
        toast.error('Failed to enable notifications');
      }
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'bg-red-500 text-white';
      case 'high': return 'bg-orange-500 text-white';
      case 'medium': return 'bg-blue-500 text-white';
      case 'low': return 'bg-green-500 text-white';
      default: return 'bg-zinc-500 text-white';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'critical':
      case 'high':
        return <AlertTriangle className="w-5 h-5" />;
      default:
        return <Info className="w-5 h-5" />;
    }
  };

  const getPriorityBorderColor = (priority) => {
    switch (priority) {
      case 'critical': return 'border-l-red-500';
      case 'high': return 'border-l-orange-500';
      case 'medium': return 'border-l-blue-500';
      case 'low': return 'border-l-green-500';
      default: return 'border-l-zinc-500';
    }
  };

  if (loading) {
    return <LoadingScreen message="Loading alerts..." />;
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white" data-testid="public-alerts-page">
      {/* Header */}
      <header className="bg-zinc-900/95 backdrop-blur-xl border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-4">
          <div className="flex items-center justify-between">
            <Logo size="md" linkTo="/" />
            <div className="flex items-center gap-3">
              <Link to="/map">
                <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
                  <Map className="w-4 h-4 mr-2" />
                  View Map
                </Button>
              </Link>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
                className="text-zinc-400 hover:text-white"
                data-testid="refresh-alerts-btn"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
              <Link to="/login">
                <Button variant="outline" size="sm" className="border-zinc-700 text-zinc-300 hover:text-white" data-testid="personnel-login-btn">
                  <LogIn className="w-4 h-4 mr-2" />
                  Personnel Login
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Push Notification Banner */}
      {isSupported && !isSubscribed && permission !== 'denied' && showPushBanner && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-red-500/20 to-orange-500/20 border-b border-red-500/30"
        >
          <div className="max-w-7xl mx-auto px-4 lg:px-6 py-3">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <BellRing className="w-5 h-5 text-red-400 animate-pulse" />
                <div>
                  <p className="text-sm font-medium text-white">Stay Alert Even When You Leave</p>
                  <p className="text-xs text-zinc-400">Enable push notifications to receive emergency alerts on your device</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleEnablePush}
                  disabled={pushLoading}
                  className="bg-red-500 hover:bg-red-600 text-white text-sm"
                  data-testid="enable-push-btn"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  Enable Alerts
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowPushBanner(false)}
                  className="text-zinc-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Subscribed Banner */}
      {isSubscribed && (
        <div className="bg-green-500/10 border-b border-green-500/30">
          <div className="max-w-7xl mx-auto px-4 lg:px-6 py-2">
            <div className="flex items-center gap-2 text-green-400 text-sm">
              <BellRing className="w-4 h-4" />
              <span>Push notifications enabled - you'll receive alerts even when this page is closed</span>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 lg:px-6 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center text-zinc-400 hover:text-white mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <h1 className="font-headings text-4xl font-bold uppercase tracking-tight text-white flex items-center gap-3">
            <Bell className="w-10 h-10 text-red-500" />
            Public Alerts
          </h1>
          <p className="text-zinc-400 mt-2">
            Stay informed about emergency alerts and road conditions in Anaconda-Deer Lodge County
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {['critical', 'high', 'medium', 'low'].map((priority) => {
            const count = notifications.filter(n => n.priority === priority).length;
            return (
              <Card key={priority} className="bg-zinc-900 border-zinc-800">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-zinc-500 uppercase tracking-wider capitalize">{priority}</p>
                      <p className="text-2xl font-bold text-white">{count}</p>
                    </div>
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getPriorityColor(priority)}`}>
                      {getPriorityIcon(priority)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Alerts List */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="font-headings text-lg uppercase tracking-wide text-white flex items-center gap-2">
              <Bell className="w-5 h-5 text-red-400" />
              All Alerts ({notifications.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {notifications.length === 0 ? (
              <EmptyState type="alerts" />
            ) : (
              <div className="space-y-4">
                {notifications.map((notif, index) => (
                  <motion.div
                    key={notif.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`p-4 rounded-lg bg-zinc-800/50 border border-zinc-700 border-l-4 ${getPriorityBorderColor(notif.priority)}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`p-2 rounded-lg ${getPriorityColor(notif.priority)}`}>
                        {getPriorityIcon(notif.priority)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={getPriorityColor(notif.priority)}>
                            {notif.priority}
                          </Badge>
                          <TimeAgo date={notif.created_at} showFull className="text-xs" />
                        </div>
                        <h3 className="text-lg font-semibold text-white mb-1">{notif.title}</h3>
                        <p className="text-zinc-400">{notif.message}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer Note */}
        <div className="mt-8 text-center">
          <p className="text-zinc-500 text-sm">
            Alerts are updated automatically. Last refreshed: {new Date().toLocaleTimeString()}
          </p>
          <p className="text-zinc-600 text-xs mt-2">
            For emergencies, call 911. This page is for informational purposes only.
          </p>
        </div>
      </main>
    </div>
  );
};

export default PublicAlertsPage;
